import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import { z } from 'zod'
import { PATIENT_FINDINGS_CONFIG, type PatientFindingConfigKey } from './PatientFinding.config'

/**
 * PatientFinding represents a validation issue/finding for a patient
 * This is a read-only view - findings are computed from patient data
 */

/**
 * PatientFindingId - direct reference to config
 * All finding IDs and metadata in one place
 */
export const PatientFindingId = PATIENT_FINDINGS_CONFIG

export type PatientFindingIdType = keyof typeof PatientFindingId

const PatientFindingGroups = Array.from(new Set(Object.values(PATIENT_FINDINGS_CONFIG).map(c => c.group)))

export type PatientFindingGroup = (typeof PatientFindingGroups)[number]

/**
 * Schema for a single finding from the patientFindingsView
 */
export const PatientFindingViewSchema = z.object({
  /**
   * Composite ID: patientId::findingId OR patientId::relationId::findingId
   * Examples:
   * - "patient_123::PharmacyMissing" (patient-level)
   * - "patient_123::relative_abc::RelativesFirstNameRequired" (per-relation)
   */
  id: z.string().readonly(),

  /**
   * The patient this finding belongs to
   */
  patientId: PatientId.ref.readonly(),

  /**
   * The specific finding identifier
   */
  findingId: z.enum(Object.keys(PATIENT_FINDINGS_CONFIG) as PatientFindingConfigKey[]),

  /**
   * The group/category this finding belongs to
   */
  findingGroup: z.enum(PatientFindingGroups).readonly(),

  /**
   * JSONPath-like reference to the field with the finding
   * Examples:
   * - "personal.firstName" (patient-level field)
   * - "relatives" (collection-level for "missing" findings)
   * - "relatives[relative_abc123].firstName" (specific relation field)
   */
  fieldPath: z.string().readonly(),

  /**
   * ID of the specific relation causing this finding
   * - null for patient-level findings (personal, address, collection "missing")
   * - relation ID for per-relation field findings
   */
  relationId: z.string().nullable().readonly(),
})

/**
 * TypeScript type for a patient finding
 */
export type PatientFindingView = z.infer<typeof PatientFindingViewSchema>
